import { useState, useCallback, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { getFluentEmojiUrl, hasFluentEmoji, getAnimatedFluentEmojiUrl, hasAnimatedFluentEmoji } from '@/lib/fluent-emoji'

interface FluentEmojiProps {
  emoji: string
  size?: number
  className?: string
  /** Whether to use native emoji as fallback on error (default: true) */
  fallbackToNative?: boolean
  /** Whether to use animated version if available (default: false) */
  animated?: boolean
  /** Priority loading for critical UI elements like navigation (default: false) */
  priority?: boolean
}

/**
 * Renders Microsoft Fluent Emoji as images for consistent cross-platform display.
 * Falls back to native emoji if the image fails to load or isn't mapped.
 * 
 * Adapted for Vite - uses standard <img> instead of next/image.
 */
// Global cache to share preloaded URLs across all FluentEmoji instances
const globalPreloadCache = new Set<string>()

export function FluentEmoji({ 
  emoji, 
  size = 24, 
  className,
  fallbackToNative = true,
  animated = false,
  priority = false
}: FluentEmojiProps) {
  const [staticError, setStaticError] = useState(false)
  const [animatedError, setAnimatedError] = useState(false)
  const [animatedLoaded, setAnimatedLoaded] = useState(() => {
    // Initialize from global cache if already preloaded
    const url = getAnimatedFluentEmojiUrl(emoji)
    return url ? globalPreloadCache.has(url) : false
  })

  const staticUrl = getFluentEmojiUrl(emoji)
  const animatedUrl = getAnimatedFluentEmojiUrl(emoji)
  const hasAnimated = !!animatedUrl

  // Reset error states when emoji changes
  useEffect(() => {
    setStaticError(false)
    setAnimatedError(false)
    // Check global cache for this URL
    if (animatedUrl && globalPreloadCache.has(animatedUrl)) {
      setAnimatedLoaded(true)
    }
  }, [emoji, animatedUrl])

  // Preload animated image ONLY when animated=true, deferred to idle time for performance
  useEffect(() => {
    // Only preload if animation is explicitly requested
    if (!animated || !animatedUrl) return
    if (typeof window === 'undefined') return // SSR guard
    
    // Already in global cache
    if (globalPreloadCache.has(animatedUrl)) {
      setAnimatedLoaded(true)
      return
    }
    
    // Defer preloading to idle time to avoid blocking initial render
    const preload = () => {
      const img = new window.Image()
      img.onload = () => {
        globalPreloadCache.add(animatedUrl)
        setAnimatedLoaded(true)
      }
      img.onerror = () => {
        setAnimatedError(true)
      }
      img.src = animatedUrl
    }
    
    // Use requestIdleCallback for deferred loading (with setTimeout fallback)
    const idleCallback = typeof window.requestIdleCallback !== 'undefined'
      ? window.requestIdleCallback(preload)
      : setTimeout(preload, 100)
    
    return () => {
      if (typeof window.requestIdleCallback !== 'undefined') {
        window.cancelIdleCallback(idleCallback as number)
      } else {
        clearTimeout(idleCallback as number)
      }
    }
  }, [animated, animatedUrl])

  const handleStaticError = useCallback(() => {
    setStaticError(true)
  }, [])

  const handleAnimatedError = useCallback(() => {
    setAnimatedError(true)
  }, [])

  // If no static URL exists or static failed, show native emoji as fallback
  if (!staticUrl || (staticError && fallbackToNative)) {
    return (
      <span 
        className={cn("inline-flex items-center justify-center shrink-0", className)}
        style={{ width: size, height: size, fontSize: size * 0.8, lineHeight: 1 }}
        role="img"
        aria-label={emoji}
      >
        {emoji}
      </span>
    )
  }

  // Determine if we should show animated version
  // Show animated when: animated prop is true, has animated URL, it's loaded, and no error
  const showAnimated = animated && hasAnimated && animatedLoaded && !animatedError

  return (
    <span 
      className={cn("relative inline-flex items-center justify-center shrink-0", className)}
      style={{ width: size, height: size }}
    >
      {/* Static version - always rendered as base layer (z-10) */}
      <img
        src={staticUrl}
        alt={emoji}
        width={size}
        height={size}
        className={cn(
          "shrink-0 absolute inset-0 z-10 transition-opacity duration-150",
          showAnimated ? "opacity-0" : "opacity-100"
        )}
        style={{ width: size, height: size }}
        onError={handleStaticError}
        loading={priority ? "eager" : "lazy"}
      />

      {/* Animated version - only render when animated prop is true to prevent unnecessary network requests */}
      {animated && animatedUrl && (
        <img
          src={animatedUrl}
          alt={emoji}
          width={size}
          height={size}
          className={cn(
            "shrink-0 absolute inset-0 z-20 transition-opacity duration-150",
            animatedLoaded && !animatedError ? "opacity-100" : "opacity-0"
          )}
          style={{ width: size, height: size }}
          onError={handleAnimatedError}
          onLoad={() => {
            if (animatedUrl) {
              globalPreloadCache.add(animatedUrl)
              setAnimatedLoaded(true)
            }
          }}
        />
      )}
    </span>
  )
}

/**
 * Hook to check if Fluent Emoji is available for a given emoji
 */
export function useFluentEmoji(emoji: string) {
  return {
    isSupported: hasFluentEmoji(emoji),
    isAnimatedSupported: hasAnimatedFluentEmoji(emoji),
    url: getFluentEmojiUrl(emoji),
    animatedUrl: getAnimatedFluentEmojiUrl(emoji),
  }
}
